import React from 'react';

const COLORS = {
  card: '#161b22',
  border: '#30363d',
  text: '#c9d1d9',
  textMuted: '#8b949e',
  green: '#3fb950',
  red: '#f85149',
  neutral: '#6e7681',
};

const LABELS = {
  oil:      '🛢️ Oil / Energy',
  fed:      '🏦 Fed Policy',
  geo:      '🌍 Geopolitics',
  consumer: '👤 Consumer',
  earnings: '📈 Earnings',
};

function sentimentCounts(scores) {
  if (!scores || scores.length === 0) return { bullish: 0, bearish: 0, neutral: 0, total: 0, mean: 0 };
  const bullish = scores.filter(v => v > 0.1).length;
  const bearish = scores.filter(v => v < -0.1).length;
  const neutral = scores.length - bullish - bearish;
  const mean = scores.reduce((s, v) => s + v, 0) / scores.length;
  return { bullish, bearish, neutral, total: scores.length, mean };
}

function SentimentBar({ bullish, neutral, bearish, total }) {
  if (total === 0) return null;
  const pct = (n) => `${((n / total) * 100).toFixed(0)}%`;
  return (
    <div style={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', marginBottom: 10 }}>
      {bearish > 0 && (
        <div title={`${bearish} bearish`} style={{ width: pct(bearish), background: COLORS.red }} />
      )}
      {neutral > 0 && (
        <div title={`${neutral} neutral`} style={{ width: pct(neutral), background: COLORS.neutral }} />
      )}
      {bullish > 0 && (
        <div title={`${bullish} bullish`} style={{ width: pct(bullish), background: COLORS.green }} />
      )}
    </div>
  );
}

function SentimentCard({ name, scores }) {
  const { bullish, bearish, neutral, total, mean } = sentimentCounts(scores);
  const meanColor = mean > 0.02 ? COLORS.green : mean < -0.02 ? COLORS.red : COLORS.textMuted;

  return (
    <div style={{
      background: COLORS.card,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 8,
      padding: '14px 16px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>
          {LABELS[name] || name}
        </div>
        <div style={{ fontSize: 11, color: COLORS.textMuted, fontFamily: 'monospace' }}>
          avg <span style={{ color: meanColor, fontWeight: 700 }}>{mean >= 0 ? '+' : ''}{mean.toFixed(3)}</span>
        </div>
      </div>

      <SentimentBar bullish={bullish} neutral={neutral} bearish={bearish} total={total} />

      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
        <span style={{ color: COLORS.red }}>🔴 {bearish} bearish</span>
        <span style={{ color: COLORS.neutral }}>⚪ {neutral} neutral</span>
        <span style={{ color: COLORS.green }}>🟢 {bullish} bullish</span>
      </div>
    </div>
  );
}

export default function ScoreDistributions({ score_distributions }) {
  if (!score_distributions) return null;
  const keys = ['oil', 'fed', 'geo', 'consumer', 'earnings'];

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ color: COLORS.textMuted, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
        Article Sentiment Breakdown
      </div>
      <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 12 }}>
        How articles scored across each indicator — bearish on the left, neutral in the middle, bullish on the right.
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
        gap: 12,
      }}>
        {keys.map(k => (
          <SentimentCard key={k} name={k} scores={score_distributions[k] || []} />
        ))}
      </div>
    </div>
  );
}
